/**
 * @description Handle ViewMore button behaviour and show hidden refinement after click on it.
 * @param {typeof import('widgets/Widget').default} Widget Base widget for extending
 */

export default function (Widget) {
    return class ProductHero extends Widget {
        init() {
            const hero = this.ref('self').get();

            if (!hero) {
                return;
            }

            // Check content width and apply mobile class if needed
            this.handleNarrowContent(hero);
            // Initialize tiles navigation
            this.initTilesNavigation(hero);
            // Initialize accordions
            this.initAccordions(hero);
            // Initialize videos
            this.initVideos(hero);
        }
        handleNarrowContent(hero) {
            const style = getComputedStyle(hero);
            const contentWidth = style.getPropertyValue('--component-width').trim();

            // Check if width is 50% or 30%
            if (contentWidth === '50%' || contentWidth === '30%') {
                hero.classList.add('b-product_hero--narrow');
            } else {
                hero.classList.remove('b-product_hero--narrow');
            }
        }
        initTilesNavigation(hero) {
            const tiles = hero.querySelectorAll('.experience-commerce_assets-productHeroTile');
            const buttonContainer = hero.querySelector('.hero-buttons');

            if (!buttonContainer) {
                return;
            }

            // Always rebuild buttons from scratch.
            buttonContainer.innerHTML = '';

            // Reset active state and choose the default active tile (first visible button-enabled tile).
            tiles.forEach((t) => t.classList.remove('active'));
            const defaultActiveTile = Array.from(tiles).find((tile) => {
                const innerTile = tile.querySelector('.b-product_hero-tile');
                const showButton = innerTile && innerTile.dataset.showButton;
                if (showButton === 'false') return false;
                const title = ((innerTile && innerTile.dataset.buttonTitle) || '').trim();
                return Boolean(title);
            }) || tiles[0];

            if (defaultActiveTile) {
                defaultActiveTile.classList.add('active');
            }

            let createdButtons = 0;
            tiles.forEach((tile) => {
                const wrapper = document.createElement('li');
                const button = document.createElement('button');
                button.setAttribute('type', 'button');
                const innerTile = tile.querySelector('.b-product_hero-tile');
                const showButton = innerTile && innerTile.dataset.showButton;
                if (showButton === 'false') {
                    return;
                }
                const buttonTitle = ((innerTile && innerTile.dataset.buttonTitle) || '').trim();
                if (!buttonTitle) {
                    return;
                }
                button.innerText = buttonTitle;
                button.setAttribute('data-tile-target', buttonTitle);
                button.setAttribute('style', buttonContainer.dataset.styles || '');
                button.setAttribute('class', buttonContainer.dataset.classes || '');
                if (tile === defaultActiveTile) {
                    button.classList.add('active');
                }
                button.addEventListener('click', this.handleTileButtonClick.bind(this, hero, tiles));
                wrapper.appendChild(button);
                buttonContainer.appendChild(wrapper);
                createdButtons += 1;
            });

            if (createdButtons === 0) {
                buttonContainer.style.display = 'none';
            } else {
                buttonContainer.style.display = '';
            }
        }

        handleTileButtonClick(hero, tiles, event) {
            const buttons = hero.querySelectorAll('.hero-buttons button');
            const target = event.currentTarget;
            if (!target || !target.dataset) {
                return;
            }
            const targetTitle = target.dataset.tileTarget;

            // Reset active states
            tiles.forEach((t) => {
                t.classList.remove('active');
                // Pause any playing videos in the current tile
                const video = t.querySelector('video');
                if (video && !video.paused) {
                    video.pause();
                    // Show play button
                    const playButton = t.querySelector('.b-product_hero-video-play');
                    if (playButton) {
                        playButton.classList.remove('hidden');
                    }
                }
            });
            buttons.forEach((b) => b.classList.remove('active'));

            // Set new active states
            target.classList.add('active');
            const targetTile = Array.from(tiles).find((t) => {
                const tileEl = t.querySelector('.b-product_hero-tile');
                return tileEl && tileEl.dataset.buttonTitle === targetTitle;
            });
            if (targetTile) {
                targetTile.classList.add('active');
            }
        }

        initAccordions(hero) {
            const accordionSelector = '.b-product_hero-accordion-trigger';
            const accordionTriggers = hero.querySelectorAll(accordionSelector);

            if (!accordionTriggers.length) {
                return;
            }

            this.ev('click', (trigger, event) => {
                event.preventDefault();

                const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
                const content = trigger.nextElementSibling;
                trigger.setAttribute('aria-expanded', String(!isExpanded));
                if (content) {
                    content.setAttribute('aria-hidden', String(isExpanded));
                }

                // Close other accordions
                if (!isExpanded) {
                    hero.querySelectorAll(accordionSelector).forEach((otherTrigger) => {
                        if (otherTrigger !== trigger) {
                            otherTrigger.setAttribute('aria-expanded', 'false');
                            if (otherTrigger.nextElementSibling) {
                                otherTrigger.nextElementSibling.setAttribute('aria-hidden', 'true');
                            }
                        }
                    });
                }
            }, accordionSelector, false);
        }

        initVideos(hero) {
            const videoWrappers = hero.querySelectorAll('.b-product_hero-video-wrapper');

            videoWrappers.forEach((wrapper) => {
                const playButton = wrapper.querySelector('.b-product_hero-video-play');
                const video = wrapper.querySelector('video');
                if (video && playButton) {
                    // Store initial state
                    video.currentTime = 0;
                    playButton.classList.remove('hidden');

                    this.ev('click', (_btn, e) => {
                        e.preventDefault();
                        if (video.paused) {
                            // Pause all other videos first
                            videoWrappers.forEach((otherWrapper) => {
                                const otherVideo = otherWrapper.querySelector('video');
                                const otherButton = otherWrapper.querySelector('.b-product_hero-video-play');
                                if (otherVideo && otherVideo !== video && !otherVideo.paused) {
                                    otherVideo.pause();
                                    if (otherButton) {
                                        otherButton.classList.remove('hidden');
                                    }
                                }
                            });
                            video.play();
                        } else {
                            video.pause();
                        }
                    }, playButton, false);

                    this.ev('play', () => {
                        playButton.classList.add('hidden');
                    }, video);

                    this.ev('pause', () => {
                        playButton.classList.remove('hidden');
                    }, video);

                    this.ev('ended', () => {
                        playButton.classList.remove('hidden');
                        video.currentTime = 0;
                    }, video);
                }
            });
        }
    };
}
